import {
  takeLatest,
  call,
  select,
  put,
  all,
  takeEvery,
} from 'redux-saga/effects';
import {
  toggleMature,
  updateContentFilter,
  toggleTheme,
  toggleThemeDone,
  updateCommentsOrder,
  updateCommentsOrderDone,
  resendVerificationEmail,
  updateEmail,
  resendVerificationEmailFail,
  resendVerificationEmailSuccess,
  updateEmailFail,
  updateEmailSuccess,
  setViewMode,
  setThemeSkin,
  setTheme,
} from './actions';
import {
  getTheme,
  getVerificationEmailAddress,
  getIsLoggedIn,
  getThemeSkin,
} from './selectors';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import {
  PapiRequestDaSharedUserSettingsCommentsOrder,
  PapiDaSharedUserSettingsCommentsOrder,
  PapiDaSharedUserUpdateEmail,
  PapiRequestDaSharedUserUpdateEmail,
  PapiDaSharedUserResendVerificationEmail,
  PapiRequestDaSharedUserResendVerificationEmail,
  PapiRequestDaDeviationSetViewMode,
  PapiDaDeviationSetViewMode,
} from '@wix/da-papi-types';
import { openUserDropdown } from '../SiteHeader/redux/actions';
import { DeviationViewMode } from '@wix/da-react-context/pkg/publicSession';
import { ThemeSkin } from '@wix/da-react-context/pkg/ThemeContext';
import { restrictLoggedOut } from '../redux/loggedout';
import ddt from '@wix/da-ddt';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { putErrorNotification } from '../utils/saga';

const logger = ddt.createLogger('publicSession');

function* toggleMatureHandler(action: ReturnType<typeof toggleMature>) {
  if (!action.payload) {
    return;
  }

  const isLoggedIn = yield select(getIsLoggedIn);

  if (!isLoggedIn) {
    yield put(restrictLoggedOut({ restrictedActionName: ReferralType.MATURE }));
    return;
  }

  const { current } = action.payload;

  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/settings/mature',
      data: { flag: !current },
    },
    () => ({
      success: true,
    })
  );
  if (result && result.success) {
    window.location.reload();
  } else {
    if (result.error_code === 0) {
      yield putErrorNotification(
        'Error, you are too young to enable mature content'
      );
      return;
    }
    yield putErrorNotification(
      'Failed to save mature setting, please try again'
    );
  }
}

function* updateContentFilterHandler(
  action: ReturnType<typeof updateContentFilter>
) {
  if (!action.payload) {
    return;
  }

  const isLoggedIn = yield select(getIsLoggedIn);
  if (!isLoggedIn) {
    yield put(restrictLoggedOut({ restrictedActionName: ReferralType.MATURE }));
    return;
  }

  const { contentFilter } = action.payload;
  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/settings/content_filter',
      data: { content_filter: contentFilter },
    },
    () => ({ success: true })
  );

  if (result && result.success) {
    window.location.reload();
  } else {
    yield putErrorNotification(
      [
        'Failed to save setting:',
        `  setting: ${contentFilter}`,
        `  ${result.errorDescription}`,
      ].join('\n')
    );
  }
}

function* saveThemeHandler() {
  // Reducer will have updated it
  const currentTheme = yield select(getTheme);
  const currentThemeSkin = yield select(getThemeSkin);

  let themeToSet = currentTheme;
  if (currentThemeSkin === ThemeSkin.GREEN) {
    themeToSet = 'green';
  }

  const result = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/theme',
      data: { theme: themeToSet },
    },
    () => ({
      success: true,
    })
  );
  if (!result || !result.success) {
    yield putErrorNotification('Failed to save theme setting');
    return;
  }
  yield put(toggleThemeDone());
}

function* updateCommentsOrderHandler({
  payload,
}: ReturnType<typeof updateCommentsOrder>) {
  if (!payload) {
    return;
  }

  const data: PapiRequestDaSharedUserSettingsCommentsOrder = {
    order: payload.order,
  };
  const result: PapiDaSharedUserSettingsCommentsOrder | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/settings/comments_order',
      data,
    },
    () => ({
      success: true,
    })
  );
  if (!result || !result.success) {
    yield putErrorNotification('Failed to save comments order');
    return;
  }
  yield put(updateCommentsOrderDone(payload.order));
}

function* resendVerificationEmailHandler() {
  yield put(openUserDropdown());

  const data: PapiRequestDaSharedUserResendVerificationEmail = {};
  const result: PapiDaSharedUserResendVerificationEmail | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/resend_verification_email',
      data,
    },
    () => ({
      success: true,
    })
  );

  if (!result || !result.success) {
    yield putErrorNotification('Failed to resend email');
    yield put(resendVerificationEmailFail());
    return;
  }

  yield put(resendVerificationEmailSuccess());
}

function* updateEmailHandler({ payload }: ReturnType<typeof updateEmail>) {
  if (!payload) {
    return;
  }
  const { email } = payload;
  const currentEmail = yield select(getVerificationEmailAddress);

  // it's as good as done so whatever
  if (email === currentEmail) {
    yield put(updateEmailSuccess(email));
    return;
  }

  const data: PapiRequestDaSharedUserUpdateEmail = { email };
  const result: PapiDaSharedUserUpdateEmail | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/user/update_email',
      data,
    },
    () => ({
      success: true,
    })
  );
  if (!result || !result.success) {
    yield put(updateEmailFail(email));
    return;
  }

  yield put(updateEmailSuccess(email));
}

function* setViewModeHandler({
  payload: { nextViewMode },
}: ReturnType<typeof setViewMode>) {
  // only save normal/theater, fullscreen is irrelevant
  if (nextViewMode === DeviationViewMode.Fullscreen) {
    return;
  }

  const data: PapiRequestDaDeviationSetViewMode = {
    view_mode: nextViewMode,
  };

  const result: PapiDaDeviationSetViewMode | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'set_view_mode',
      data,
    },
    () => ({ success: true }),
    'dadeviation'
  );

  if (!result || isApiError(result)) {
    logger.error('Failed to persist new view mode', nextViewMode);
  }
}

export default function* saga() {
  yield all([
    takeLatest(toggleMature, toggleMatureHandler),
    takeLatest(updateContentFilter, updateContentFilterHandler),
    takeLatest(toggleTheme, saveThemeHandler),
    takeLatest(setTheme, saveThemeHandler),
    takeLatest(setThemeSkin, saveThemeHandler),
    takeLatest(updateCommentsOrder, updateCommentsOrderHandler),
    takeLatest(resendVerificationEmail, resendVerificationEmailHandler),
    takeLatest(updateEmail, updateEmailHandler),
    takeEvery(setViewMode, setViewModeHandler),
  ]);
}
